import { z } from 'zod';
import { IncludeTestWorkspacesQuerySchema } from './test-workspaces.schemas';
import { OrgRoleSlugSchema, WorkspaceRoleSlugSchema } from '../identity/members.schemas';
import { PendingInvitationSchema } from '../identity/invitations.schemas';

export const CreateAdminWorkspaceBodySchema = z.object({
  name: z.string().trim().min(2).max(80),
  // Marks the workspace as a non-production testing environment, surfacing the
  // app-wide yellow testing banner. Defaults to a real (non-test) workspace.
  isTest: z.boolean().default(false),
  owner: z.object({
    email: z.email(),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
  }),
  address: z.object({
    street: z.string().trim().min(1).max(100),
    houseNumber: z.string().trim().min(1).max(20),
    postalCode: z
      .string()
      .trim()
      .regex(/^\d{5}$/),
    city: z.string().trim().min(1).max(80),
    country: z.string().trim().min(1).max(80),
    latitude: z.number().min(-90).max(90).nullable().default(null),
    longitude: z.number().min(-180).max(180).nullable().default(null),
  }),
});

export type CreateAdminWorkspaceBody = z.infer<typeof CreateAdminWorkspaceBodySchema>;

export const CreateAdminWorkspaceResponseSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  owner: z.object({
    memberId: z.uuid(),
    email: z.email(),
  }),
});

export type CreateAdminWorkspaceResponse = z.infer<typeof CreateAdminWorkspaceResponseSchema>;

export const AdminWorkspaceListItemSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  // Marks the workspace as a non-production testing environment. Carried on the
  // list item so the workspace list can badge test tenants and the edit form can
  // prefill its checkbox (GetAdminWorkspaceResponseSchema inherits it via .extend).
  isTest: z.boolean(),
  createdAt: z.iso.datetime(),
  // Null for the HLRE workspace, which has no owner.
  owner: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.email(),
    })
    .nullable(),
});

export type AdminWorkspaceListItem = z.infer<typeof AdminWorkspaceListItemSchema>;

// The only list parameter today: the operator app's test-workspace filter
// (IMM-593.2). See `test-workspaces.schemas.ts` for its semantics.
export const ListAdminWorkspacesQuerySchema = IncludeTestWorkspacesQuerySchema;
export type ListAdminWorkspacesQuery = z.infer<typeof ListAdminWorkspacesQuerySchema>;

export const ListAdminWorkspacesResponseSchema = z.object({
  data: z.array(AdminWorkspaceListItemSchema),
});

export type ListAdminWorkspacesResponse = z.infer<typeof ListAdminWorkspacesResponseSchema>;

export const GetAdminWorkspacePathParamsSchema = z.object({
  id: z.uuid(),
});

export const UpdateAdminWorkspaceBodySchema = z.object({
  name: z.string().trim().min(2).max(80),
  // Optional so an omitted field means UNCHANGED. A non-optional boolean would
  // make every plain rename silently clear the workspace's test mark.
  isTest: z.boolean().optional(),
});

export type UpdateAdminWorkspaceBody = z.infer<typeof UpdateAdminWorkspaceBodySchema>;

export const UpdateAdminWorkspaceResponseSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  isTest: z.boolean(),
});

export type UpdateAdminWorkspaceResponse = z.infer<typeof UpdateAdminWorkspaceResponseSchema>;

export const UpdateAdminWorkspacePathParamsSchema = z.object({
  id: z.uuid(),
});

export const GetAdminWorkspaceResponseSchema = AdminWorkspaceListItemSchema.extend({
  address: z.object({
    street: z.string(),
    houseNumber: z.string(),
    postalCode: z.string(),
    city: z.string(),
    country: z.string(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
  }),
  organization: z.object({
    name: z.string(),
    type: z.literal('main'),
  }),
  // The roles the workspace's kind offers, so the invite dialog never lists one the backend refuses.
  offeredRoles: z.object({
    workspaceRoles: z.array(WorkspaceRoleSlugSchema),
    organisationRoles: z.array(OrgRoleSlugSchema),
  }),
});

export type GetAdminWorkspaceResponse = z.infer<typeof GetAdminWorkspaceResponseSchema>;

export const CreateAdminWorkspaceInvitationPathParamsSchema = z.object({
  workspaceId: z.uuid(),
});

// Admin-issued invite. Mirrors the workspace-tier CreateWorkspaceMemberBody: a
// single organisation role granted across every selected Standort plus an
// optional workspace role. Issued with a null member-issuer (the platform admin
// is not a workspace member); the anti-escalation guard is N/A here.
export const CreateAdminWorkspaceInvitationBodySchema = z
  .object({
    email: z.email(),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    // A workspace without Standort roles (HLRE) invites with the workspace role alone.
    organisationRole: OrgRoleSlugSchema.optional(),
    workspaceRole: WorkspaceRoleSlugSchema.optional(),
    organizationIds: z.array(z.uuid()).default([]),
    primaryOrganizationId: z.uuid().nullable().default(null),
  })
  .refine((body) => body.organisationRole !== undefined || body.workspaceRole !== undefined, {
    path: ['organisationRole'],
    message: 'organisationRole or workspaceRole is required',
  });

export type CreateAdminWorkspaceInvitationBody = z.infer<typeof CreateAdminWorkspaceInvitationBodySchema>;

export const CreateAdminWorkspaceInvitationResponseSchema = PendingInvitationSchema;

export type CreateAdminWorkspaceInvitationResponse = z.infer<typeof CreateAdminWorkspaceInvitationResponseSchema>;
